/**
 * Your own Microsoft Teams connection: connect an account, then pick any number of
 * chats, group chats, or channels to hear about your runs in — every one gets notified
 * independently, so one failing destination never blocks the others.
 *
 * Entirely personal and additive — a project can separately have its own Teams webhook
 * for a shared channel; this is on top of that, not instead of it.
 */

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { TeamsTargetOption } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { TeamsTargetPicker } from "@/components/TeamsTargetPicker";
import { Badge, Empty, Loading, Notice } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

export function TeamsConnection() {
  const [params] = useSearchParams();
  const oauthError = params.get("error") ?? undefined;

  const { data: connection, loading: connectionLoading, reload: reloadConnection } = useAsync(
    () => endpoints.teamsConnection(),
    [],
  );
  const { data: targets, reload: reloadTargets } = useAsync(
    () => endpoints.teamsTargetSet(),
    [connection?.connected],
    { enabled: !!connection?.connected },
  );

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const connect = () => {
    window.location.href = "/api/me/teams/connect";
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await endpoints.disconnectTeams();
      reloadConnection();
    } finally {
      setBusy(false);
    }
  };

  const add = async (option: TeamsTargetOption) => {
    await endpoints.addTeamsTarget(option);
    reloadTargets();
  };

  const remove = async (targetId: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.removeTeamsTarget(targetId);
      reloadTargets();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (connectionLoading) return <Loading rows={3} label="Loading your Teams connection" />;

  const list = targets ?? [];
  const selectedRefs = new Set(list.map((t) => t.graph_ref));

  return (
    <div className="stack" style={{ gap: 16, maxWidth: 560 }}>
      <Link to="/account/apps" className="muted" style={{ fontSize: 12.5 }}>
        ← Apps
      </Link>
      <h1 style={{ marginBottom: 0 }}>Microsoft Teams</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Connect your own Microsoft account to get a Teams message when your runs finish,
        on top of whatever a project's own Teams channel already announces. Add as many
        destinations as you like — each one is notified independently.
      </p>

      {oauthError && <Notice tone="danger">{oauthError}</Notice>}

      <div className="card">
        <div className="card__header">
          <span className="card__title">Connection</span>
        </div>
        <div className="card__body stack" style={{ gap: 14 }}>
          {!connection?.connected ? (
            <Empty title="Not connected">
              <button type="button" className="button button--primary" onClick={connect}>
                Connect Microsoft Teams
              </button>
            </Empty>
          ) : (
            <>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 550 }}>{connection.account_email}</div>
                  {connection.needs_reconnect && (
                    <Badge tone="danger">Needs reconnecting</Badge>
                  )}
                </div>
                <button
                  type="button"
                  className="button button--small button--danger"
                  disabled={busy}
                  onClick={disconnect}
                >
                  Disconnect
                </button>
              </div>

              {connection.needs_reconnect && (
                <Notice tone="danger">
                  Microsoft Graph rejected the last message — the connection was likely
                  revoked. Reconnect to keep getting notified.
                </Notice>
              )}

              {connection.needs_reconnect && (
                <button type="button" className="button" onClick={connect}>
                  Reconnect
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {connection?.connected && !connection.needs_reconnect && (
        <div className="card">
          <div className="card__header">
            <span className="card__title">Where to notify you</span>
          </div>
          <div className="card__body stack" style={{ gap: 12 }}>
            {problem && <Notice tone="danger">{problem}</Notice>}

            {list.length === 0 ? (
              <span className="muted" style={{ fontSize: 12.5 }}>Nothing added yet</span>
            ) : (
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                {list.map((target) => (
                  <span
                    key={target.id}
                    className="row"
                    style={{ gap: 4, alignItems: "center" }}
                  >
                    <Badge tone="accent">{target.label}</Badge>
                    <button
                      type="button"
                      className="button button--small button--ghost"
                      disabled={busy}
                      onClick={() => remove(target.id)}
                      aria-label={`Remove ${target.label}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <TeamsTargetPicker
              selectedRefs={selectedRefs}
              onAdd={add}
              busy={busy}
              buttonLabel={list.length ? "Add another" : "Add a chat or channel"}
            />
          </div>
        </div>
      )}
    </div>
  );
}
